/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from "react";
import { SelectInput } from "components/Select";
import { useStoreEmailFilterActions } from "../../filter/hooks/actions";
import { useFilterState } from "../../filter/hooks/filterState";
import {
  useGetStoresOptionsQuery,
  useGetStoresResponsiblesOptionsQuery,
} from "@/services/store/select-options";
import { useGetJustificationActiveQuery } from "@/services/justification";
import { RoleEnum } from "@/enum/role";
import { getSelectClasses } from "@/utils/filter-styles";
import { formatOptionLabel } from "@/utils/format-option-label";
import { DATA_TYPE, DataTypeFilter } from "../../filter/types";
import { CONCILIATION_DATE_RANGE_FILTER_KEY } from "../../utils/constants";
import { FilterSelect } from "@/components/ui/filter-select";
import {
  PAYROLL_VOUCHER_STATUS_LABELS,
  PayrollVoucherStatusEnum,
} from "@/enum/payroll-voucher-status";
import { PAYROLL_VOUCHER_STATUS_FILTER } from "../../utils/messages";
import {
  divergenceStatusOptions,
  nsuStatusOptions,
} from "../../utils/status-options";
import { compactMultiValueFor } from "./compact-multi-value";
import { ChevronDown, ChevronUp, SlidersHorizontal, X } from "lucide-react";

const dataTypeOptions = [
  { value: DATA_TYPE.DIVERGENCE, label: "Divergências" },
  { value: DATA_TYPE.NSU, label: "NSUs" },
];

const typeOptions = [
  { value: "true", label: "Perda" },
  { value: "false", label: "Desconto em Folha" },
];

const payrollVoucherStatusOptions = [
  ...Object.values(PayrollVoucherStatusEnum).map(
    (status: PayrollVoucherStatusEnum) => ({
      value: status as string,
      label: PAYROLL_VOUCHER_STATUS_LABELS[status],
    })
  ),
  {
    value: PAYROLL_VOUCHER_STATUS_FILTER.NONE,
    label: PAYROLL_VOUCHER_STATUS_FILTER.NONE_LABEL,
  },
];

interface FilterConfig {
  key: string;
  options: any[];
  placeholder: string;
  label: string;
  value: any[];
  onChange: (option: any) => void;
}

interface SelectFiltersProps {
  searchInput?: JSX.Element;
}

export default function SelectFilters({ searchInput }: SelectFiltersProps) {
  const { updateFilter, changeDataType } = useStoreEmailFilterActions();
  const filterState = useFilterState();
  const { filters } = filterState || { filters: {} };
  const isInitialized = useRef(false);
  const [showMore, setShowMore] = useState(false);

  const ToggleChevron = showMore ? ChevronUp : ChevronDown;
  const isDivergenceMode: boolean = filters.dataType === DATA_TYPE.DIVERGENCE;

  const STATUS_OPTIONS_BY_TYPE: Record<string, typeof divergenceStatusOptions> = {
    [DATA_TYPE.DIVERGENCE]: divergenceStatusOptions,
    [DATA_TYPE.NSU]: nsuStatusOptions,
  };
  const statusOptions = STATUS_OPTIONS_BY_TYPE[filters.dataType as string] ?? divergenceStatusOptions;

  const { data: storeOptions } = useGetStoresOptionsQuery(
    undefined,
    { refetchOnMountOrArgChange: false }
  );
  const { data: responsibleOptions } = useGetStoresResponsiblesOptionsQuery(
    { role: RoleEnum.ANALISTA },
    { refetchOnMountOrArgChange: false }
  );
  const { data: justificationsData } = useGetJustificationActiveQuery();

  const justificationOptions: any[] = justificationsData?.map((j: any) => ({
    value: j.id.toString(),
    label: j.name,
  })) || [];

  const getStoredFilters = (): any => JSON.parse(localStorage.getItem("manage-loss-filters") || "{}");
  const setStoredFilters = (f: any): void => localStorage.setItem("manage-loss-filters", JSON.stringify(f));

  useEffect(() => {
    if (!isInitialized.current) {
      const savedFilters = getStoredFilters();
      Object.entries(savedFilters).forEach(([key, value]) => {
        updateFilter({ key, value });
      });
      isInitialized.current = true;
    }
  }, [updateFilter]);

  const handleFilterChange = (key: string) => (option: any) => {
    const value = Array.isArray(option)
      ? option.length > 0 ? option.map((opt: any) => opt.value) : undefined
      : option?.value;

    updateFilter({ key, value });
    updateFilter({ key: "meta", value: { page: 1, perPage: filters?.meta?.perPage || 10 } });

    const currentFilters = getStoredFilters();
    setStoredFilters({ ...currentFilters, [key]: value });
  };

  const handleDataTypeChange = (newDataType: DataTypeFilter): void => {
    changeDataType(newDataType);
    const currentFilters = getStoredFilters();
    setStoredFilters({ ...currentFilters, dataType: newDataType });
  };

  const getCurrentSelectedOptions = (key: string, options: any[]): any[] => {
    const filterValue = filters[key];
    if (!filterValue) return [];
    if (Array.isArray(filterValue)) {
      return options.filter(option => filterValue.includes(option.value));
    }
    const singleOption = options.find(option => option.value === filterValue);
    return singleOption ? [singleOption] : [];
  };

  const primaryFilters: FilterConfig[] = [
    {
      key: "store_code",
      options: storeOptions || [],
      placeholder: "Loja: todas",
      label: "Loja",
      value: getCurrentSelectedOptions("store_code", storeOptions || []),
      onChange: handleFilterChange("store_code"),
    },
    {
      key: "status",
      options: statusOptions,
      placeholder: "Divergência: todas",
      label: "Divergência",
      value: getCurrentSelectedOptions("status", statusOptions),
      onChange: handleFilterChange("status"),
    },
    ...(isDivergenceMode ? [{
      key: "payroll_voucher_status",
      options: payrollVoucherStatusOptions,
      placeholder: "RH: todos",
      label: "RH",
      value: getCurrentSelectedOptions("payroll_voucher_status", payrollVoucherStatusOptions),
      onChange: handleFilterChange("payroll_voucher_status"),
    }] : []),
  ];

  const secondaryFilters: FilterConfig[] = [
    {
      key: "dataType",
      options: dataTypeOptions,
      placeholder: "Tipo",
      label: "Tipo",
      value: getCurrentSelectedOptions("dataType", dataTypeOptions),
      onChange: (option: any) => handleDataTypeChange(option?.value || DATA_TYPE.DIVERGENCE),
    },
    {
      key: "analyst",
      options: responsibleOptions || [],
      placeholder: "Conferente: todos",
      label: "Conferente",
      value: getCurrentSelectedOptions("analyst", responsibleOptions || []),
      onChange: handleFilterChange("analyst"),
    },
    ...(isDivergenceMode ? [{
      key: "justification_id",
      options: justificationOptions,
      placeholder: "Justificativa: todas",
      label: "Justificativa",
      value: getCurrentSelectedOptions("justification_id", justificationOptions),
      onChange: handleFilterChange("justification_id"),
    }] : []),
    {
      key: "type",
      options: typeOptions,
      placeholder: "Contábil: todos",
      label: "Contábil",
      value: getCurrentSelectedOptions("type", typeOptions),
      onChange: handleFilterChange("type"),
    },
  ];

  const activeSecondaryFilters: { key: string; label: string; displayValue: string }[] =
    secondaryFilters
      .filter(f => f.value.length > 0)
      .map(f => ({
        key: f.key,
        label: f.label,
        displayValue: f.value.map(v => v.label).join(", "),
      }));

  const hasPeriod: boolean = Boolean(filters.conciliation_date_range?.start);

  const totalActiveFilters: number =
    primaryFilters.filter(f => f.value.length > 0).length +
    activeSecondaryFilters.length +
    Number(hasPeriod);

  const clearSecondaryFilter = (key: string): void => {
    if (key === "dataType") {
      handleDataTypeChange(DATA_TYPE.DIVERGENCE);
      return;
    }
    updateFilter({ key, value: undefined });
    const currentFilters = getStoredFilters();
    setStoredFilters({ ...currentFilters, [key]: undefined });
  };

  const clearAllFilters = (): void => {
    secondaryFilters.forEach(f => clearSecondaryFilter(f.key));
    primaryFilters.forEach(f => {
      updateFilter({ key: f.key, value: undefined });
    });
    updateFilter({ key: CONCILIATION_DATE_RANGE_FILTER_KEY, value: undefined });
    localStorage.removeItem("manage-loss-filters");
  };

  const renderSelect = (filter: FilterConfig): JSX.Element => {
    const hasValue: boolean = filter.value.length > 0;
    return (
      <SelectInput
        key={filter.key}
        options={filter.options}
        placeholder={filter.placeholder}
        value={filter.value}
        onChange={filter.onChange}
        className={`${getSelectClasses(hasValue)} h-9 min-w-[160px] max-w-[200px] text-sm`}
        classNamePrefix="an-simple-select"
        aria-label={filter.label}
        isMulti
        isClearable
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        components={{ MultiValue: compactMultiValueFor(filter.label) }}
        formatOptionLabel={formatOptionLabel}
      />
    );
  };

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex items-center gap-2 flex-wrap">
        {searchInput}
        {primaryFilters.map(renderSelect)}

        <button
          type="button"
          onClick={() => setShowMore(!showMore)}
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3 h-9 text-sm font-medium text-gray-600 transition-colors [border:1px_solid_rgb(209,213,219)] hover:bg-gray-50"
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          Mais filtros
          {totalActiveFilters > 0 && (
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#2E0D1E] text-[10px] font-bold text-white">
              {totalActiveFilters}
            </span>
          )}
          <ToggleChevron className="h-3.5 w-3.5" />
        </button>

        {totalActiveFilters > 0 && (
          <button
            type="button"
            onClick={clearAllFilters}
            className="ml-auto text-sm text-gray-500 hover:text-gray-700 whitespace-nowrap"
          >
            Limpar filtros
          </button>
        )}
      </div>

      {showMore && (
        <div className="flex items-center gap-2 flex-wrap rounded-lg border border-gray-200 bg-gray-50/50 p-3">
          <FilterSelect
            options={dataTypeOptions}
            value={(filters.dataType as DataTypeFilter) || DATA_TYPE.DIVERGENCE}
            onChange={handleDataTypeChange}
            placeholder="Tipo"
          />
          {secondaryFilters.filter(f => f.key !== "dataType").map(renderSelect)}
        </div>
      )}

      {activeSecondaryFilters.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {activeSecondaryFilters.map(tag => (
            <span
              key={tag.key}
              className="inline-flex items-center gap-1 rounded-full bg-[#2E0D1E]/10 px-2.5 py-1 text-xs font-medium text-[#2E0D1E]"
            >
              {tag.label}: {tag.displayValue}
              <button
                type="button"
                onClick={() => clearSecondaryFilter(tag.key)}
                className="ml-0.5 rounded-full p-0.5 hover:bg-[#2E0D1E]/20 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
